import { BaseAction } from "../../../core/actions/BaseAction";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookingTypeTrip, IBookedOffer, IBookingResponse, IExchangeOfferRequest, IDeterminePassenger, IQuickExchangeOfferRequest } from "../../../Orchestrator/Types/Index";
import dayjs from "dayjs";
import { getAllOfferWithoutExchangeAction } from "../../LogicActions/CommonActions/GetAllOfferWithoutExchangeAction";
import { IGetAllTripWithoutExchangeActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/FilterTripAction";
import { getAllTripWithoutExchangeAction } from "../../LogicActions/CommonActions/GetAllTripWithoutExchangeAction";
import { fetchExchangeOffersAction } from "../../LogicActions/ChangeDate/FetchExchangeOffersAction";
import { getPassengerAge } from "../../../Lib/Utils/validationUtil";
import { TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { preferTrainTrips } from "../../../Lib/Utils/booking";

export class ChangeDateSelectorChangeAction extends BaseAction<void, {
    date: string,
    fullDate: string,
    currentTrainType: string,
    dateSelectionState: [dayjs.Dayjs, dayjs.Dayjs],
    booking: IBookingResponse,
    selectedPassengerForCancel: string[],
    selectedPassengerForCancelInBound: string[]
}> {

    override async run(): Promise<void | null> {
        let result: any;
        const { date, fullDate, currentTrainType, dateSelectionState, booking, selectedPassengerForCancel, selectedPassengerForCancelInBound } = this.params!;
        // updateRecoilState("selectedChangeDateAtom", date);
        updateRecoilState("selectedChangeDateAtom", dayjs(fullDate));
    
        if (currentTrainType === TrainTypeEnum.outbound) {
            updateRecoilState("selectedRangeStateAtom", [
                dayjs(new Date(fullDate).toISOString()),
                dateSelectionState[1],
            ]);
        } else {
            updateRecoilState("selectedRangeStateAtom", [
                dateSelectionState[0],
                dayjs(new Date(fullDate).toISOString()),
            ]);
        }

        const anonymousPassengerSpecifications =
            booking?.booking?.passengers.filter(p =>
                currentTrainType === TrainTypeEnum.outbound
                    ? selectedPassengerForCancel.includes(p.id)
                    : selectedPassengerForCancelInBound.includes(p.id)
            ).map((passenger) => ({
                id: passenger.id,
                type: passenger.type,
                externalRef: passenger.externalRef,
                age: passenger.age,
                cards: passenger?.cards || [],
                prmNeeds: passenger?.prmNeeds || [],
            })) || [];

        await getAllOfferWithoutExchangeAction.setParams(booking?.booking?.bookedOffers)
        await getAllOfferWithoutExchangeAction.execute()
        let bookingOfferWithoutExchange: IBookedOffer[] = getAllOfferWithoutExchangeAction.result!

        const params: IGetAllTripWithoutExchangeActionParams = {
            bookedOffers: bookingOfferWithoutExchange,
            trips: booking?.booking?.trips
        }
        await getAllTripWithoutExchangeAction.setParams(params)
        await getAllTripWithoutExchangeAction.execute()
        const allTripWithoutExchange: IBookingTypeTrip[] = getAllTripWithoutExchangeAction.result!

        const outboundBookedOfferOnly = bookingOfferWithoutExchange.filter(
            (offer) =>
                preferTrainTrips(allTripWithoutExchange).find((trip) => trip.direction === (currentTrainType === TrainTypeEnum.outbound ? TripTypeEnum.OUT_BOUND : TripTypeEnum.IN_BOUND))
                    ?.id === offer?.tripCoverage?.coveredTripId
        );

        let admissions = outboundBookedOfferOnly?.flatMap(offer =>
            offer?.admissions?.filter((admission) =>
                admission.status === "FULFILLED" &&
                admission.passengerIds.some(pid =>
                    anonymousPassengerSpecifications.some(p => p.id === pid)
                )
            ) || []
        );

        const fulfillmentIds: string[] = [];

        admissions?.forEach(element => {
            fulfillmentIds.push(element.id);
        });

        // Calculate original price of items being exchanged
        let originalPriceOfExchangedItemsOutbound = 0;
        if (booking?.booking?.bookedOffers && fulfillmentIds.length > 0) {
            booking.booking.bookedOffers.forEach(offer => {
                offer.admissions?.forEach(admission => {
                    if (fulfillmentIds.includes(admission.id)) {
                        originalPriceOfExchangedItemsOutbound += admission.price.amount;
                    }
                });
            });
        }

        if (originalPriceOfExchangedItemsOutbound > 0) {
            const scale = booking.booking.bookedOffers[0]?.admissions[0]?.price?.scale;
            updateRecoilState((currentTrainType == TrainTypeEnum.outbound) ? "originalPriceOfExchangedItemsOutboundAtom" : "originalPriceOfExchangedItemsInboundAtom", null);
            updateRecoilState((currentTrainType == TrainTypeEnum.outbound) ? "originalPriceOfExchangedItemsOutboundAtom" : "originalPriceOfExchangedItemsInboundAtom", (originalPriceOfExchangedItemsOutbound / ((100 * scale) / scale)));
        }

        const returnSearchParamsDate = ((currentTrainType === TrainTypeEnum.outbound ? dateSelectionState?.[1] : dateSelectionState?.[0])?.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") || null)

        // Find the actual trip based on direction
        const targetDirection = currentTrainType === TrainTypeEnum.outbound ? TripTypeEnum.OUT_BOUND : TripTypeEnum.IN_BOUND;
        const targetTrip = preferTrainTrips(allTripWithoutExchange).find((trip) => trip.direction === targetDirection);
        const matchingTrip = targetTrip
            ? booking?.booking?.trips.find(trip => trip.id === targetTrip.id)
            : null;

        const fallbackTrip = preferTrainTrips(booking?.booking?.trips).find(t => t.direction === targetDirection);

        const exchangeOffersRequest: IQuickExchangeOfferRequest = {
            fulfillmentIds,
            overruleCode: "",
            tripSearchCriteria: {
                departureTime: fullDate
                    ? dayjs(fullDate).format("YYYY-MM-DD") + "T00:00:00"
                    : "",
                origin: {
                    objectType: "StopPlaceRef",
                    stopPlaceRef: matchingTrip?.origin.stopPlaceRef || fallbackTrip?.origin.stopPlaceRef || "",
                },
                destination: {
                    objectType: "StopPlaceRef",
                    stopPlaceRef: matchingTrip?.destination.stopPlaceRef || fallbackTrip?.destination.stopPlaceRef || "",
                },
                // returnSearchParameters: null,
                direction: currentTrainType == "inbound" ? TripTypeEnum.IN_BOUND : TripTypeEnum.OUT_BOUND,
            },
            offerSearchCriteria: {
                currency: "AED",
            },
            anonymousPassengerSpecifications:
                anonymousPassengerSpecifications.map((passenger) => {
                    // const pass: IDeterminePassenger = {
                    //     age: passenger.age || 0,
                    //     cards: passenger?.cards || [],
                    //     prmNeeds: ((passenger?.prmNeeds || []).length > 0) ? passenger?.prmNeeds[0] : ""
                    // }
                    // const passengerAge = getPassengerAge(pass)
                    return {
                        externalRef: passenger.externalRef,
                        type: passenger.type,
                        cards: passenger?.cards || [],
                        prmNeeds: passenger.prmNeeds || [],
                        age: passenger?.age //passengerAge,
                    }
                }),
        };

        try {
            updateRecoilState("offerAtom", []);
            await fetchExchangeOffersAction.setParams(
                {
                    booking_id: booking?.booking.id,
                    exchangeOffersRequest: exchangeOffersRequest,
                    booking: booking,
                    originalPriceOfExchangedItems: originalPriceOfExchangedItemsOutbound,
                    trainType: currentTrainType,
                    selectedDate: dateSelectionState[0]?.format("YYYY-MM-DD"),
                    inboundSelectedDate: dateSelectionState[1]?.format("YYYY-MM-DD")
                }
            )
            await fetchExchangeOffersAction.execute()
        } catch (error) {
            console.error("Error fetching exchange offers:", error);
        }
    }
}

export const changeDateSelectorChangeAction = new ChangeDateSelectorChangeAction(); 